import React, { Component } from "react";
import { Button, Drawer, Form, Input, Select, Spin, Steps, message } from "antd";
import { puapMakes } from "./dropdown/puapMakes";
import { puapModels } from "./dropdown/puapModels";
import { puapParts } from "./dropdown/puapParts";
import { puapYears } from "./dropdown/puapYears";
import API from "../../API/API";
import { Navigate } from "react-router-dom";
import { ROUTES } from "../../Routes/Routes";
import { PlusOutlined } from "@ant-design/icons";

const { Step } = Steps;
const { Option } = Select;

class AddLead extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      currentStep: 0,
      formValues: {},
      selectedMake: "",
      modelsList: [],
      modelSearch: "",
      partsList: [...puapParts],
      loginAgent: {},
      redirectToLogin: false,
      selectedPart: "",
      customPart: "",
      loading: false,
      checkingCustomer: false,
      isExistingCustomer: false,
      customerStatusMessage: "",
      availableSalesAgents: [],
      salesAgentsLoaded: false,
      previousAgentId: null,
      previousAgentName: "",
      selectedAgentId: null,
      agentSelectionChanged: false,
    };
    this.formRef = React.createRef();
    this.checkTimeout = null;
    this.lastCheckedPhone = null;
  }
  componentWillUnmount() {
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout);
    }
  }
  componentDidMount() {
    const UD = localStorage.getItem("userData");
    if (!UD) {
      this.setState({ redirectToLogin: true });
    } else {
      this.setState({ loginAgent: JSON.parse(UD) }, this.fetchAvailableSalesAgents);
    }
  }

  showDrawer = () => {
    this.setState({ visible: true });
    this.fetchAvailableSalesAgents();
  };

  closeDrawer = () => {
    this.setState({
      visible: false,
      currentStep: 0,
      formValues: {},
      isExistingCustomer: false,
      customerStatusMessage: "",
      previousAgentId: null,
      previousAgentName: "",
      selectedAgentId: null,
      agentSelectionChanged: false,
    });
    this.formRef.current.resetFields();
  };

  getLoggedInAgentId = (agent = this.state.loginAgent) => agent?.id || agent?._id || null;

  isAvailableSalesAgent = (agentId, agents = this.state.availableSalesAgents) => {
    if (!agentId) return false;
    return agents.some((agent) => String(agent._id) === String(agentId));
  };

  getDefaultSalesAgentId = (agent = this.state.loginAgent, agents = this.state.availableSalesAgents) => {
    const agentId = this.getLoggedInAgentId(agent);
    return agent?.role === "SALES-AGENT" && this.isAvailableSalesAgent(agentId, agents)
      ? String(agentId)
      : null;
  };

  fetchAvailableSalesAgents = async () => {
    try {
      const response = await API.userGetAll();
      const availableSalesAgents = (response?.data || []).filter((user) => (
        user.role === "SALES-AGENT" &&
        user.isActive === true &&
        user.isDeleted === false &&
        user.isPerformance === true
      ));

      this.setState((previousState) => {
        const previousAgentIsAvailable = previousState.previousAgentId &&
          availableSalesAgents.some((agent) => (
            String(agent._id) === String(previousState.previousAgentId)
          ));
        const shouldDefaultToPreviousAgent = previousState.isExistingCustomer &&
          previousAgentIsAvailable &&
          !previousState.agentSelectionChanged;
        const defaultSalesAgentId = this.getDefaultSalesAgentId(
          previousState.loginAgent,
          availableSalesAgents
        );
        const shouldDefaultToLoggedInSalesAgent = !previousState.isExistingCustomer &&
          !previousState.agentSelectionChanged &&
          defaultSalesAgentId;

        return {
          availableSalesAgents,
          salesAgentsLoaded: true,
          selectedAgentId: shouldDefaultToPreviousAgent
            ? String(previousState.previousAgentId)
            : shouldDefaultToLoggedInSalesAgent || previousState.selectedAgentId || null
        };
      });
    } catch (error) {
      console.error("Failed to fetch available sales agents", error);
      this.setState({ salesAgentsLoaded: true });
    }
  };

  handleSalesAgentChange = (selectedAgentId) => {
    if (this.state.loginAgent?.role !== "ADMIN") return;
    this.setState({ selectedAgentId: selectedAgentId || null, agentSelectionChanged: true });
  };

  handleMakeChange = (value) => {
    this.setState({
      selectedMake: value,
      modelsList: puapModels[value] || [],
      modelSearch: "",
    });
    this.formRef.current.setFieldsValue({ model: undefined });
  };

  formatPhoneNumber = (value) => {
    let cleaned = value.replace(/\D/g, "").slice(0, 11);

    if (cleaned.startsWith("1")) {
      cleaned = cleaned.slice(1);
    }

    let formattedNumber = "+1";

    if (cleaned.length > 0) formattedNumber += ` (${cleaned.slice(0, 3)}`;
    if (cleaned.length >= 4) formattedNumber += `) ${cleaned.slice(3, 6)}`;
    if (cleaned.length >= 7) formattedNumber += `-${cleaned.slice(6)}`;

    return formattedNumber;
  };

  validatePhoneNumber = (_, value) => {
    if (!value) return Promise.reject("Enter a valid 10-digit US phone number.");
    const cleaned = value.replace(/\D/g, "");
    return cleaned.length === 11 ? Promise.resolve() : Promise.reject("Enter a valid 10-digit US phone number.");
  };

  capitalizeWords = (value) => {
    return value
      .replace(/[^a-zA-Z0-9\s'’.-]/g, "")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Inbound placeholder email is built from the phone number (e.g. 19167657621@puap.com),
  // so every customer gets a unique email. Agents can still replace it with a real email.
  getPhoneDigitsWithCountryCode = (phone) => {
    let cleaned = (phone || "").replace(/\D/g, "");
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      cleaned = cleaned.slice(1);
    }
    return cleaned.length === 10 ? `1${cleaned}` : "";
  };

  generateEmailFromPhone = (phone) => {
    const digits = this.getPhoneDigitsWithCountryCode(phone);
    return digits ? `${digits}@puap.com` : "";
  };

  // Empty or @puap.com emails are system placeholders; a real email typed by the agent is never overwritten.
  isPlaceholderEmail = (email) => {
    const value = (email || "").trim().toLowerCase();
    return !value || value.endsWith("@puap.com");
  };

  syncPlaceholderEmailWithPhone = (phone) => {
    if (!this.formRef.current) return;
    const currentEmail = this.formRef.current.getFieldValue("email");
    if (!this.isPlaceholderEmail(currentEmail)) return;
    this.formRef.current.setFieldsValue({ email: this.generateEmailFromPhone(phone) });
  };

  // ✅ CHECK CUSTOMER API CALL - ALTERNATIVE VERSION
  checkExistingCustomer = async (phone) => {
    try {
      this.setState({
        checkingCustomer: true,
        isExistingCustomer: false,
        customerStatusMessage: "Checking customer...",
        previousAgentId: null,
        previousAgentName: "",
        selectedAgentId: null,
        agentSelectionChanged: false,
      });

      console.log("Checking customer with phone:", phone);

      // Send the cleaned 10-digit number to backend
      let cleaned = phone.replace(/\D/g, "");

      // Remove leading 1 if present
      if (cleaned.length === 11 && cleaned.startsWith("1")) {
        cleaned = cleaned.slice(1);
      }

      // Ensure it's 10 digits
      if (cleaned.length !== 10) {
        this.setState({
          checkingCustomer: false,
          customerStatusMessage: "Please enter a valid 10-digit phone number"
        });
        return;
      }

      // Make API call
      const result = await API.CheckCustomerByPhone(cleaned);

      console.log("API Result:", result);

      // Since CallApi returns data directly, we need to check the structure
      if (result.success) {
        // Customer found - EXISTING CUSTOMER
        const {
          fullName,
          email,
          zipCode,
          phoneNumber,
          previousAgentId,
          previousAgentName
        } = result.customer;
        const normalizedPreviousAgentId = previousAgentId ? String(previousAgentId) : null;
        const previousAgentIsAvailable = this.isAvailableSalesAgent(normalizedPreviousAgentId);

        // Show success notification
        message.success("Existing customer detected. Edit details if needed.", 4);

        // Old shared placeholder emails (e.g. na@puap.com) are replaced with the phone-based email
        const customerEmail = this.isPlaceholderEmail(email)
          ? this.generateEmailFromPhone(cleaned)
          : email;

        // Update form fields with customer data
        this.formRef.current.setFieldsValue({
          fullName: fullName || "",
          email: customerEmail,
          zipCode: zipCode || "",
          phoneNumber: phoneNumber || this.formatPhoneNumber(cleaned)
        });

        this.setState({
          isExistingCustomer: true,
          customerStatusMessage: "👤 Existing customer detected. Edit details if needed.",
          previousAgentId: normalizedPreviousAgentId,
          previousAgentName: previousAgentName || "",
          selectedAgentId: previousAgentIsAvailable
            ? normalizedPreviousAgentId
            : null,
          agentSelectionChanged: false,
          formValues: {
            ...this.state.formValues,
            fullName: fullName || "",
            email: customerEmail,
            zipCode: zipCode || "",
          },
        });

      } else if (result.message === "Customer not found.") {
        // Customer not found - NEW CUSTOMER
        message.info("New customer. Please enter details.", 4);
        // ✅ RESET FORM FIELDS FOR NEW CUSTOMER
        this.formRef.current.setFieldsValue({
          fullName: "",  // Clear name
          email: "",     // Clear email
          zipCode: "",   // Clear zip code
          // Keep phone number as user typed it
        });

        this.setState({
          isExistingCustomer: false,
          customerStatusMessage: "🆕 New customer. Please enter details.s",
          previousAgentId: null,
          previousAgentName: "",
          selectedAgentId: this.getDefaultSalesAgentId(),
          agentSelectionChanged: false,
          formValues: {
            ...this.state.formValues,
            fullName: "",  // Clear from state too
            email: "",     // Clear from state too
            zipCode: "",   // Clear from state too
          },
        });

        // Default the email to the phone-based placeholder (editable by the agent)
        this.formRef.current.setFieldsValue({ email: this.generateEmailFromPhone(cleaned) });

      } else {
        // Other errors (400, 500, etc.)
        message.warning(result.message || "Error checking customer", 3);

        this.setState({
          customerStatusMessage: result.message ? "⚠️ " + result.message : "❌ Error checking customer",
        });
      }

    } catch (error) {
      console.error("Customer lookup failed with error:", error);

      // Network or unexpected error
      message.error("Connection error. Please try again.", 3);

      this.setState({
        customerStatusMessage: "❌ Connection error",
      });
    } finally {
      this.setState({ checkingCustomer: false });
    }
  };


  nextStep = () => {
    this.formRef.current
      .validateFields()
      .then((values) => {
        this.setState((prevState) => ({
          formValues: { ...prevState.formValues, ...values },
          currentStep: prevState.currentStep + 1,
        }));
      })
      .catch(() => console.log("Please fill required fields correctly."));
  };

  prevStep = () => {
    this.setState((prevState) => ({ currentStep: prevState.currentStep - 1 }));
  };

  onFinish = async () => {
    this.setState({ loading: true });

    try {
      const latestValues = await this.formRef.current.validateFields();
      const allValues = { ...this.state.formValues, ...latestValues };
      const selectedPart = allValues.part === "Others" ? allValues.customPart : allValues.part;
      // Only an admin sends an assignee; the backend resolves it for everyone else.
      const isAdmin = this.state.loginAgent?.role === "ADMIN";
      const selectedInboundAgentId = isAdmin && this.isAvailableSalesAgent(this.state.selectedAgentId)
        ? this.state.selectedAgentId
        : null;

      const payload = {
        personalInfo: {
          fullName: allValues.fullName.trim(),
          phoneNumber: allValues.phoneNumber,
          email: allValues.email,
          zipCode: allValues.zipCode || "",
        },
        vehicleInfo: {
          year: allValues.year,
          make: allValues.make,
          model: allValues.model,
          part: selectedPart,
          vin: allValues.vin,
        },
        domain: "inbound call",
        leadType: "inbound",
        ...(selectedInboundAgentId ? { handledBy: selectedInboundAgentId } : {}),
      };

      const response = await API.SubmitLead(payload);
      if (response.lead.success) {
        message.success("Lead submitted successfully!");
        this.formRef.current.resetFields();
        this.setState({
          visible: false,
          currentStep: 0,
          formValues: {},
          isExistingCustomer: false,
          customerStatusMessage: "",
          previousAgentId: null,
          previousAgentName: "",
          selectedAgentId: null,
          agentSelectionChanged: false,
        });

        if (this.props.refreshLeads) {
          this.props.refreshLeads();
        }
      } else {
        message.error(response.message || "Failed to submit lead.");
      }
    } catch (error) {
      console.log("Please fix form errors before submitting.");
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      currentStep,
      visible,
      modelsList,
      partsList,
      redirectToLogin,
      loginAgent,
      isExistingCustomer,
      availableSalesAgents,
      salesAgentsLoaded,
      previousAgentId,
      previousAgentName,
      selectedAgentId,
    } = this.state;
    const isAdmin = loginAgent?.role === "ADMIN";
    const isSalesAgent = loginAgent?.role === "SALES-AGENT";
    const canSelectInboundAgent = isAdmin;
    const previousAgentIsAvailable = this.isAvailableSalesAgent(previousAgentId);
    const selectedAgentIsAvailable = this.isAvailableSalesAgent(selectedAgentId);

    if (redirectToLogin) {
      return <Navigate to={ROUTES.LOGIN} />;
    }

    return (
      <div>
        {/* {JSON.parse(localStorage.getItem("userData"))?.role === "SALES-AGENT" && ( */}
        <Button type="primary" onClick={this.showDrawer}>
          <PlusOutlined size={20} /> Inbound Lead
        </Button>
        {/* )} */}

        <Drawer title="Submit Lead" width={500} onClose={this.closeDrawer} open={visible}>

          <Steps current={currentStep}>
            <Step title="Vehicle Info" />
            <Step title="Personal Info" />
          </Steps>

          <Form ref={this.formRef} layout="vertical" style={{ marginTop: "21px" }}>
            {currentStep === 0 && (
              <>
                <Form.Item name="year" label="Year" rules={[{ required: true, message: "Select year" }]}>
                  <Select placeholder="Select year" showSearch>
                    {puapYears.map((year) => (
                      <Option key={year} value={year}>{year}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="make" label="Make" rules={[{ required: true, message: "Select make" }]}>
                  <Select placeholder="Select make" showSearch onChange={this.handleMakeChange}>
                    {puapMakes.map((make) => (
                      <Option key={make} value={make}>{make}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="model" label="Model" rules={[{ required: true, message: "Select or type a model" }]}>
                  <Select
                    placeholder="Select model"
                    showSearch
                    onSearch={(value) => this.setState({ modelSearch: value })}
                    onChange={() => this.setState({ modelSearch: "" })}
                    onOpenChange={(open) => {
                      if (!open) this.setState({ modelSearch: "" });
                    }}
                  >
                    {modelsList.map((model) => (
                      <Option key={model} value={model}>{model}</Option>
                    ))}
                    {(() => {
                      // Model not in the list: let the agent use the typed text
                      const typed = (this.state.modelSearch || "").trim();
                      const inList = modelsList.some((m) => m.toLowerCase() === typed.toLowerCase());
                      if (!typed || !this.state.selectedMake || inList) return null;
                      return (
                        <Option key="__custom_model__" value={typed}>
                          {`Not in list? Use: "${typed}"`}
                        </Option>
                      );
                    })()}
                  </Select>
                </Form.Item>

                <Form.Item name="part" label="Part" rules={[{ required: true, message: "Select or enter a part" }]}>
                  <Select
                    showSearch
                    placeholder="Select or type a part"
                    mode="combobox"
                    value={this.state.selectedPart}
                    onChange={(value) => {
                      this.setState({
                        selectedPart: value,
                        customPart: value === "Others" ? "" : value,
                      });
                      this.formRef.current.setFieldsValue({ part: value });
                    }}
                  >
                    {partsList.map((part) => (
                      <Option key={part} value={part}>{part}</Option>
                    ))}
                  </Select>
                </Form.Item>

                {this.state.selectedPart === "Others" && (
                  <Form.Item
                    name="customPart"
                    rules={[{ required: true, message: "Enter custom part" }]}
                  >
                    <Input
                      placeholder="Enter custom part"
                      value={this.state.customPart}
                      onChange={(e) => {
                        this.setState({ customPart: e.target.value });
                        this.formRef.current.setFieldsValue({ customPart: e.target.value });
                      }}
                    />
                  </Form.Item>
                )}

                <Form.Item
                  name="vin"
                  label="VIN Number"
                  rules={[
                    {
                      pattern: /^[A-Z0-9]{17}$/,
                      message: "enter a correct Vin number",
                    },
                  ]}
                >
                  <Input
                    maxLength={17}
                    placeholder="VIN number"
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                      this.formRef.current.setFieldsValue({ vin: value });
                    }}
                  />
                </Form.Item>
              </>
            )}

            {currentStep === 1 && (
              <>
                <Form.Item
                  name="phoneNumber"
                  label="Phone Number"
                  rules={[{ required: true, message: "Enter valid phoneNumber", validator: this.validatePhoneNumber }]}
                >
                  <Input
                    maxLength={18}
                    placeholder="+1 (XXX) XXX-XXXX"
                    onChange={async (e) => {
                      const formatted = this.formatPhoneNumber(e.target.value);
                      this.formRef.current.setFieldsValue({ phoneNumber: formatted });
                      this.syncPlaceholderEmailWithPhone(formatted);

                      const cleaned = formatted.replace(/\D/g, "");

                      // Check if we have at least 10 digits (US number)
                      let phoneDigits = cleaned;
                      if (cleaned.length === 11 && cleaned.startsWith("1")) {
                        phoneDigits = cleaned.slice(1);
                      }

                      // ✅ Clear previous customer status AND form fields when user starts typing
                      if (phoneDigits.length < 10) {
                        this.setState({
                          isExistingCustomer: false,
                          customerStatusMessage: "",
                          previousAgentId: null,
                          previousAgentName: "",
                          selectedAgentId: null,
                          agentSelectionChanged: false,
                        });

                        // Also clear name and email if phone is being changed significantly
                        if (phoneDigits.length < 7) { // If less than area code + prefix
                          this.formRef.current.setFieldsValue({
                            fullName: "",
                            email: "",
                            zipCode: "",
                          });
                        }
                      }

                      if (phoneDigits.length === 10) {
                        // Clear previous timeout
                        if (this.checkTimeout) {
                          clearTimeout(this.checkTimeout);
                        }

                        // Set timeout for debouncing
                        this.checkTimeout = setTimeout(async () => {
                          if (this.lastCheckedPhone !== phoneDigits) {
                            this.lastCheckedPhone = phoneDigits;
                            await this.checkExistingCustomer(phoneDigits);
                          }
                        }, 800);
                      } else {
                        this.lastCheckedPhone = null;
                      }
                    }}
                    onBlur={async () => {
                      const phoneValue = this.formRef.current.getFieldValue('phoneNumber');
                      if (phoneValue) {
                        let cleaned = phoneValue.replace(/\D/g, "");
                        if (cleaned.length === 11 && cleaned.startsWith("1")) {
                          cleaned = cleaned.slice(1);
                        }

                        if (cleaned.length === 10 && this.lastCheckedPhone !== cleaned) {
                          this.lastCheckedPhone = cleaned;
                          await this.checkExistingCustomer(cleaned);
                        }
                      }
                    }}
                    suffix={this.state.checkingCustomer ? <Spin size="small" /> : null}
                  />
                </Form.Item>

                {/* ADD THIS: Customer Status Display */}
                {this.state.customerStatusMessage && (
                  <div style={{
                    marginBottom: 16,
                    padding: "8px 12px",
                    borderRadius: 4,
                    backgroundColor: this.state.isExistingCustomer ? "#f6ffed" : "#e6f7ff",
                    border: `1px solid ${this.state.isExistingCustomer ? "#b7eb8f" : "#91d5ff"}`,
                    color: this.state.isExistingCustomer ? "#389e0d" : "#1890ff",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}>
                    {this.state.isExistingCustomer ? (
                      <span style={{ fontSize: 16 }}></span>
                    ) : (
                      <span style={{ fontSize: 16 }}></span>
                    )}
                    {this.state.customerStatusMessage}
                  </div>
                )}

                {(isExistingCustomer || canSelectInboundAgent) && (
                  <div style={{
                    marginBottom: 16,
                    padding: "12px",
                    borderRadius: 4,
                    backgroundColor: "#f0f5ff",
                    border: "1px solid #adc6ff"
                  }}>
                    {isExistingCustomer && (
                      <>
                        <div style={{ marginBottom: 4 }}>
                          <strong>Previous Sales Agent:</strong>{" "}
                          {previousAgentName || "Unavailable"}
                          {salesAgentsLoaded && !previousAgentIsAvailable ? " (Unavailable)" : ""}
                        </div>
                        <div style={{ marginBottom: canSelectInboundAgent || isSalesAgent ? 12 : 0 }}>
                          {canSelectInboundAgent
                            ? selectedAgentIsAvailable
                              ? "This lead will be assigned to the selected Sales Agent."
                              : "This lead will remain unassigned unless a Sales Agent is selected."
                            : previousAgentIsAvailable
                              ? "This lead is locked to the previous Sales Agent."
                              : "Previous Sales Agent is unavailable. This lead will be assigned to you."}
                        </div>
                      </>
                    )}

                    {canSelectInboundAgent && (
                      <Form.Item label="Assign To" style={{ marginBottom: 0 }}>
                        <Select
                          value={selectedAgentIsAvailable ? selectedAgentId : undefined}
                          onChange={this.handleSalesAgentChange}
                          placeholder="Leave unassigned"
                          loading={!salesAgentsLoaded}
                          allowClear
                        >
                          {availableSalesAgents.map((agent) => (
                            <Option key={agent._id} value={agent._id}>
                              {`${agent.firstName || ""} ${agent.lastName || ""}`.trim() || agent.userName}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}

                    {isSalesAgent && isExistingCustomer && (
                      <Form.Item label="Assign To" style={{ marginBottom: 0 }}>
                        <Input
                          value={previousAgentIsAvailable
                            ? (previousAgentName || "Previous Sales Agent")
                            : "You (previous Sales Agent unavailable)"}
                          disabled
                        />
                      </Form.Item>
                    )}
                  </div>
                )}

                <Form.Item
                  name="fullName"
                  label="Full Name"
                  rules={[{ required: true, message: "Enter full name" }]}
                >
                  <Input
                    placeholder="Enter full name"
                    onChange={(e) => {
                      const capitalized = this.capitalizeWords(e.target.value);
                      this.formRef.current.setFieldsValue({ fullName: capitalized });
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ required: true, type: "email", message: "Enter valid email" }]}
                >
                  <Input placeholder="Enter email" />
                </Form.Item>

                <Form.Item
                  name="zipCode"
                  label="Zip Code"
                  rules={[{ pattern: /^\d{0,64}$/, message: "Enter a valid zip code" }]}
                >
                  <Input maxLength={6} placeholder="Enter zip code (optional)" />
                </Form.Item>
              </>
            )}

            <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "21px" }}>
              {currentStep > 0 && <Button onClick={this.prevStep} disabled={this.state.loading}>Previous</Button>}
              {currentStep < 1 && <Button type="primary" onClick={this.nextStep} disabled={this.state.loading}>Next</Button>}
              {currentStep === 1 && <Button type="primary" onClick={this.onFinish} disabled={this.state.loading}>Submit</Button>}
            </div>
          </Form>
        </Drawer>
      </div>
    );
  }
}

export default AddLead;
